import { create } from "zustand";
import { Catalog } from "../../../domains/catalog/aggregates/Catalog";
import { ProdutoTipo } from "../../../Infrastructure/interfaces/PedidoMeep";
import { Product } from "../../../domains/catalog/aggregates/Product";
import { CatalogRepository } from "../../../Infrastructure/repositories/api/CatalogRepository";
import { ICardGroupDiscounts } from "domains/cashless/aggregates/ICardCashless";
import { toast as toastfy } from "react-toastify";
import { RefObject } from "react";
import { Store } from "domains/catalog/aggregates/Store";
import { SeccionedProduct } from "domains/catalog/aggregates/SeccionedProduct";
import { getGlobalAddLog } from "Infrastructure/repositories/appInsights/AppInsights";

const addLog = getGlobalAddLog();

interface CatalogState {
  suggestionProductIds: string[];
  rawCatalog: Catalog | null;
  catalog: Catalog | null;
  catalogRestrictions: string[];
  groupDiscounts: ICardGroupDiscounts[];
  modalValues: {
    open: boolean;
    openWeighing?: boolean;
    ref?: RefObject<HTMLDivElement>;
    product?: Product;
  };
  openProductDetail: (
    _productId: string,
    _refItem?: RefObject<HTMLDivElement>
  ) => void;
  closeProductDetail: () => void;
  sync: (localId: string, eventId?: string) => void;
  forceSync: (eventId?: string) => Promise<void>;
  getConfigurationProduct: (productType: ProdutoTipo) => Product | null;
  handleRestrictions: (restrictions: string[]) => void;
  handleDiscounts: (discounts: ICardGroupDiscounts[]) => void;
}

const catalogRepository = CatalogRepository();

export const useCatalogStore = create<CatalogState>((set, get) => {
  const filterCatalog = (
    catalog: Catalog | null,
    restrictions?: string[],
    discounts?: ICardGroupDiscounts[]
  ) => {
    if (!catalog) return null;

    const state = get();
    const catalogRestrictions = restrictions ?? state.catalogRestrictions;
    const groupDiscounts = discounts ?? state.groupDiscounts;

    return {
      ...catalog,
      stores: catalog.stores.map((store) => ({
        ...store,
        seccionedProduct: store.seccionedProduct
          .map((section) => ({
            ...section,
            products: section.products
              .filter((product) => !catalogRestrictions.includes(product.id))
              .map((product) => {
                const discount = groupDiscounts.find(
                  (d: ICardGroupDiscounts) => d.produtoId === product.id
                );
                return discount
                  ? { ...product, discount: discount.desconto }
                  : product;
              }),
          }))
          .filter((section) => section.products.length > 0),
      })),
    };
  };

  const sync = async (localId: string, eventId?: string) => {
    try {
      const isChange = await catalogRepository.syncIfDiffAsync(localId);
      if (isChange) {
        const _catalogSync = await catalogRepository.syncAsync(eventId ?? undefined);
        if (_catalogSync) {
          set({ rawCatalog: _catalogSync, catalog: filterCatalog(_catalogSync) });
        }
      }
    } catch (error) {
      addLog && addLog("Catalog", error);
    }
  };

  const forceSync = async (eventId?: string) => {
    const _catalogSync = await catalogRepository.syncAsync(eventId ?? undefined);
    if (_catalogSync) {
      set({ rawCatalog: _catalogSync, catalog: filterCatalog(_catalogSync) });
    }
  };

  const getConfigurationProduct = (productType: ProdutoTipo) => {
    return (
      get().rawCatalog?.configProducts.find(
        (configProduct: Product) => configProduct.type === productType
      ) ?? null
    );
  };

  const handleRestrictions = (restrictions: string[]) => {
    set((state: CatalogState) => ({
      catalogRestrictions: restrictions,
      catalog: filterCatalog(state.rawCatalog, restrictions),
    }));
  };

  const handleDiscounts = (discounts: ICardGroupDiscounts[]) => {
    set((state: CatalogState) => ({
      groupDiscounts: discounts,
      catalog: filterCatalog(state.rawCatalog, undefined, discounts),
    }));
  };

  const openProductDetail = (
    _productId: string,
    _refItem?: RefObject<HTMLDivElement>
  ) => {
    const _store = get().catalog?.stores.find((s: Store) =>
      s.seccionedProduct.some((sp) =>
        sp.products.some((product) => product.id === _productId)
      )
    );

    if (_store?.isClosed) {
      toastfy.error("Loja fechada");
    } else {
      const product = _store?.seccionedProduct
        .flatMap((sp: SeccionedProduct) => sp.products)
        .find((product: Product) => product.id === _productId);

      if (product) {
        if (product.type === ProdutoTipo.Kilo) {
          set({ modalValues: { open: false, openWeighing: true, product, ref: _refItem } });
        } else {
          set({ modalValues: { open: true, product, ref: _refItem } });
        }
      } else {
        toastfy.error("Produto não encontrado");
      }
    }
  };

  const closeProductDetail = () => {
    const { modalValues } = get();
    const store = get().catalog?.stores.find((s: Store) =>
      s.seccionedProduct.some((sp) =>
        sp.products.includes(modalValues.product!)
      )
    );

    if (store?.isClosed) {
      toastfy.error("Loja fechada");
    } else {
      set({ modalValues: { open: false, openWeighing: false } });
    }
  };

  catalogRepository.getAsync().then((catalog) => {
    if (catalog) {
      set({
        rawCatalog: catalog,
        catalog: filterCatalog(catalog),
        suggestionProductIds: catalog?.cartRecommendationByLocal ?? [],
      });
    }
  });

  return {
    suggestionProductIds: [],
    rawCatalog: null,
    catalog: null,
    catalogRestrictions: [],
    groupDiscounts: [],
    modalValues: { open: false },
    openProductDetail,
    closeProductDetail,
    sync,
    forceSync,
    getConfigurationProduct,
    handleRestrictions,
    handleDiscounts,
  };
});
